<template>
  <v-app
    v-if="$auth.user"
  >
    <v-banner
      v-if="appEnv !== 'production'"
      max-width="490px"
      width="100%"
      max-height="75px"
      class="py-1"
      position="fixed"
      rounded
      data-html2canvas-ignore
      style="bottom: var(--v-safe-area-bottom); right: 1px; z-index: 2147483647; background: rgba(255, 0, 0, 0.4);color: #fff; pointer-events: none; user-select: none"
    >
      <div class="text-center">
        You are viewing a development version of this site (environment: {{ appEnv }}<template v-if="appVersion">
          , v{{ appVersion }}
        </template><template v-if="appBuildNumber">
          build {{ appBuildNumber }}
        </template>)
      </div>
    </v-banner>
    <app-error-messages />

    <AppRail
      v-if="$vuetify.display.mdAndUp && !hideHeader"
    />

    <AppHeader
      v-if="!hideHeader"
    />

    <RouterLoader
      v-if="isNavigating"
    />
    
    <v-main
      class="position-relative"
      :style="{paddingBottom: appEnv !== 'production' ? '54px' : null}"
      :class="{
        'ml-24': $vuetify.display.mdAndUp
      }"
    >
      <AccountStatusBanner class="mt-3 mx-4" />

      <slot />
    </v-main>
  </v-app>
</template>
<script setup lang="ts">
import { inject } from "vue"

const isNavigating = inject('isNavigating')
</script>

<script lang="ts">
import {defineComponent} from "vue"
import {mapActions} from "pinia"
import AppErrorMessages from "@/components/AppErrorMessages.vue"
import {CalendarViews} from "@/enums/calendarViews"
import {useCalendarStore} from "@/stores/calendar"
import moment from "@/utils/moment"
import AppHeader from "@/components/Header/AppHeader.vue"
import AppRail from "@/components/Header/AppRail.vue"
import RouterLoader from "@/components/RouterLoader.vue"
import AccountStatusBanner from "@/components/AccountStatusBanner.vue"

export default defineComponent({
  props: {
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AccountStatusBanner,
    AppErrorMessages,
    AppHeader,
    AppRail,
    RouterLoader
  },
  data() {
    return {
      profileMenuOpen: false,
      showAccountSwitch: false,
      loading: '',
    }
  },
  mounted() {
    const currentUrl = window.location.href
    const newUrl = currentUrl.split('?')[0]

    //Case when coming from the daily forecast link
    const userId = parseInt(this.$route.query['id']) || null
    const date = this.$route.query['date'] || null
    if (userId && userId !== this.$auth.user?.id) {
      this.profileMenuOpen = true
      this.showAccountSwitch = true
    }
    if(date) {
      const start = moment(date)
      this.setDate(CalendarViews.DAY, start)
    }

    if(userId || date) {
      history.replaceState({}, '', newUrl)
    }
  },
  methods: {
    ...mapActions(useCalendarStore, ['setDate']),
  },
  computed: {
    appEnv() {
      return import.meta.env.VITE_APP_ENV
    },
    appVersion() {
      return import.meta.env.VITE_APP_VERSION
    },
    appBuildNumber() {
      return import.meta.env.VITE_APP_BUILD_NUMBER
    },
    userTitleFont() {
      return this.$auth.user?.settings?.title_font || 'Outfit'
    },
    userParagraphFont() {
      return this.$auth.user?.settings?.paragraph_font || 'Filson Pro'
    },
    fontSizeAdjust() {
      return this.$auth.user?.settings?.font_size == 'Large' ? 0.56 : this.$auth.user?.settings?.font_size == 'Medium' ? 0.53 : 'unset'
    },
    primaryFontColor() {
      return this.$auth.user?.settings?.primary_font_color == '#111110' ? 'unset' : this.$auth.user?.settings?.primary_font_color || 'inherit'
    },
    secondaryFontColor() {
      return this.$auth.user?.settings?.secondary_font_color == '#727272' ? 'unset' : this.$auth.user?.settings?.secondary_font_color || 'inherit'
    },
  }
})
</script>

<style lang="scss">
// global styles from user settings
h1, h2, h3, h4, h5, h6 {
  font-family: v-bind(userTitleFont);
  color: v-bind(primaryFontColor);
}

div, span {
  &:not(.v-btn &, h1 &, h2 &, h3 &, h4 &, h5 &, h6 &) {
    font-family: v-bind(userParagraphFont);
    font-size-adjust: v-bind(fontSizeAdjust);
    color: v-bind(primaryFontColor);
  }
}

#app {
  .text-secondary {
    color: v-bind(secondaryFontColor) !important;
  }

  // On mobile, clip any phantom horizontal overflow (e.g. multi-day all-day
  // event titles that extend past the visible 2-column area) so the page
  // doesn't gain a horizontal scrollbar.
  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')} {
    overflow-x: clip;
  }
}
</style>


<style scoped lang="scss">

:deep() .nav-menu {
  transform: translateX(-50px);

  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')} {
    transform: translateX(-30px);
  }
}

:deep(.profile-image-wrapper + .v-badge__badge) {
  background: transparent;
}

.page-toggle:deep() {
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 999px;

  .v-btn {
    min-width: unset;
    padding: 0 7px;
    transition: none;

    &.active {
      background-color: rgb(var(--v-theme-primary));
      color: white;
    }
  }
  .v-btn__content {
    font-size: 20px;

    .btn-text {
      font-size: 14px;
      max-width: 0px;
      margin-left: 0;
      overflow: hidden;

      transition: max-width 0.3s, margin-left 0.3s;

      &.shown {
        max-width: 100px;
        margin-left: 5px;
      }
    }
  }
}
</style>
